/**
 * useVoiceSession Hook
 *
 * Extracts voice session management logic from VoiceSession.tsx
 * into a reusable hook. Both the legacy VoiceSession component and
 * the new learner ConversationScreen can consume this hook.
 *
 * Returns: status, transcript, phaseState, isMuted, startSession(), endSession(), toggleMute()
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  createVapSession,
  type VapSession,
  type VapSessionStatus,
  type VapTranscriptTurn,
  type PhaseState,
} from '../lib/gemini';
import {
  getVoiceSessionToken,
  getRecommendedMode,
} from '../lib/gemini/token-service';
import type { PhaseConfig } from '../lib/gemini/provider';

export interface UseVoiceSessionOptions {
  /** System prompt defining agent behavior */
  agentPrompt: string;
  /** Assessment ID for token scoping */
  assessmentId: string;
  /** Auth token (learner JWT from URL) */
  authToken?: string;
  /** Phase configuration for warm-up -> in-character flow */
  phaseConfig?: PhaseConfig;
}

export interface UseVoiceSessionReturn {
  status: VapSessionStatus;
  transcript: VapTranscriptTurn[];
  phaseState: PhaseState | null;
  isMuted: boolean;
  error: string | null;
  /** Start (or restart) the voice session */
  startSession: () => Promise<VapTranscriptTurn[] | void>;
  /** End the session and return final transcript */
  endSession: () => Promise<VapTranscriptTurn[]>;
  /** Toggle mute state */
  toggleMute: () => void;
}

export function useVoiceSession({
  agentPrompt,
  assessmentId,
  authToken,
  phaseConfig,
}: UseVoiceSessionOptions): UseVoiceSessionReturn {
  const [status, setStatus] = useState<VapSessionStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [transcript, setTranscript] = useState<VapTranscriptTurn[]>([]);
  const [phaseState, setPhaseState] = useState<PhaseState | null>(null);

  const sessionRef = useRef<VapSession | null>(null);

  const handlePhaseChange = useCallback((state: PhaseState) => {
    setPhaseState(state);
  }, []);

  const startSession = useCallback(async () => {
    setError(null);
    setTranscript([]);
    setIsMuted(false);
    setPhaseState(null);

    // Get authentication token
    let token: string;
    try {
      const mode = getRecommendedMode();
      const tokenResult = await getVoiceSessionToken({
        mode,
        tokenEndpoint: import.meta.env.VITE_VOICE_TOKEN_ENDPOINT,
        assessmentId,
        authToken,
      });
      token = tokenResult.token;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get authentication token';
      setError(message);
      setStatus('error');
      return;
    }

    const effectiveSystemPrompt = phaseConfig?.systemPrompt ?? agentPrompt;

    const session = createVapSession({
      token,
      systemPrompt: effectiveSystemPrompt,
      phaseConfig,
      onStatusChange: (newStatus) => {
        setStatus(newStatus);
      },
      onTranscriptUpdate: (turn) => {
        setTranscript((prev) => [...prev, turn]);
      },
      onPhaseChange: handlePhaseChange,
      onError: (err) => {
        setError(err.message);
      },
    });

    sessionRef.current = session;

    try {
      await session.connect();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to connect');
      setError(error.message);
      setStatus('error');
    }
  }, [agentPrompt, assessmentId, authToken, phaseConfig, handlePhaseChange]);

  const endSession = useCallback(async (): Promise<VapTranscriptTurn[]> => {
    if (!sessionRef.current) {
      return [];
    }

    try {
      const finalTranscript = await sessionRef.current.disconnect();
      setTranscript(finalTranscript);
      sessionRef.current = null;
      return finalTranscript;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to end session');
      console.error('[useVoiceSession] Failed to end session:', error);
      setError(error.message);
      setStatus('error');
      sessionRef.current = null;
      throw error;
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (sessionRef.current) {
      const newMuted = !isMuted;
      sessionRef.current.setMuted(newMuted);
      setIsMuted(newMuted);
    }
  }, [isMuted]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        sessionRef.current.disconnect();
        sessionRef.current = null;
      }
    };
  }, []);

  return {
    status,
    transcript,
    phaseState,
    isMuted,
    error,
    startSession,
    endSession,
    toggleMute,
  };
}
